import { moduleType, sectionType, TiptapContent } from "regen-api-ts-client";

import { SystemContentType } from "../../types";
import { DATASOURCES, MapInfo } from "../InteractiveMap/types";

export type FieldViewProps = {
  farmId: string;
  fieldId: string;
  onClickField: (fieldId: string) => void;
};

export interface Section {
  ids: (sectionType | SystemContentType)[];
  name: string;
  defaultClosed?: boolean;
  hasFieldSelector?: boolean;
  mapInfo?: MapInfo;
  publishedContent?: { [id: string]: TiptapContent };
}

export type Module = {
  id: moduleType;
  name: string;
  icon: string;
  implemented: boolean;
};

export const MODULES: Module[] = [
  {
    id: moduleType.DASHBOARD,
    name: "Home",
    icon: "/left-nav-icons/home.svg",
    implemented: true,
  },
  {
    id: moduleType.FIELD_PROPERTIES,
    name: "Field Properties",
    icon: "/left-nav-icons/field-properties.svg",
    implemented: true,
  },
  {
    id: moduleType.COVER_CROPS,
    name: "Cover Crops",
    icon: "/left-nav-icons/cover-crops.svg",
    implemented: true,
  },
  {
    id: moduleType.NUTRIENT_MANAGEMENT,
    name: "Nutrient Management",
    icon: "/left-nav-icons/nutrient-management.svg",
    implemented: true,
  },
  {
    id: moduleType.TILLAGE,
    name: "Tillage",
    icon: "/left-nav-icons/tillage.svg",
    implemented: true,
  },
  {
    id: moduleType.SOIL_HEALTH,
    name: "Soil Health",
    icon: "/left-nav-icons/soil-health.svg",
    implemented: true,
  },
];

export const SYSTEM_CONTENT_SECTIONS: { [key in SystemContentType]: Section } =
  {
    [SystemContentType.DASHBOARD_SYSTEM_CONTENT]: {
      ids: ["dashboard_system_content"],
      name: "Dashboard System Content",
      defaultClosed: false,
    },
    [SystemContentType.COVER_CROPS_BACKGROUND]: {
      ids: ["cover_crops_background"],
      name: "Background",
      defaultClosed: true,
    },
    [SystemContentType.COVER_CROPS_BENEFITS]: {
      ids: ["cover_crops_benefits"],
      name: "Learn about cover crop benefits",
      defaultClosed: true,
    },
    [SystemContentType.COVER_CROPS_ROI_BACKGROUND]: {
      ids: ["cover_crops_roi_background"],
      name: "Return on investment from your cover crops",
      defaultClosed: false,
    },
    [SystemContentType.COVER_CROPS_NUTRIENT_COMPOSITION_BACKGROUND]: {
      ids: ["cover_crops_nutrient_composition_background"],
      name: "Cover crop nutrient composition",
      defaultClosed: false,
    },
    [SystemContentType.NUTRIENT_MANAGEMENT_BACKGROUND]: {
      ids: ["nutrient_management_background"],
      name: "Approach",
      defaultClosed: true,
    },
    [SystemContentType.TILLAGE_BACKGROUND]: {
      ids: ["tillage_background"],
      name: "Deep Tillage Background",
      defaultClosed: true,
    },
    [SystemContentType.TILLAGE_APPROACH]: {
      ids: ["tillage_approach"],
      name: "Approach",
      defaultClosed: true,
    },
    [SystemContentType.TILLAGE_UNDERSTANDING_COMPACTION]: {
      ids: ["tillage_understanding_compaction"],
      name: "Understanding your compaction analysis",
      defaultClosed: true,
    },
    [SystemContentType.SOIL_HEALTH_BACKGROUND]: {
      ids: ["soil_health_background"],
      name: "Soil Health Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_ZONES_BACKGROUND]: {
      ids: ["soil_health_zones_background"],
      name: "Soil Health Zones Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_TEST_RESULTS_BACKGROUND]: {
      ids: ["soil_health_test_results_background"],
      name: "Soil Health Test Results Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_RESPIRATION_BACKGROUND]: {
      ids: ["soil_health_respiration_background"],
      name: "Soil Respiration (Haney) Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_WEOC_BACKGROUND]: {
      ids: ["soil_health_weoc_background"],
      name: "Water Extractable Organic Carbon (Haney) Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_WEON_BACKGROUND]: {
      ids: ["soil_health_weon_background"],
      name: "Water Extractable Organic N (Haney) Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_H3A_ORGANIC_P_BACKGROUND]: {
      ids: ["soil_health_h3a_organic_p_background"],
      name: "H3A Organic P (Haney) Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_MACROAGGREGATES_BACKGROUND]: {
      ids: ["soil_health_macroaggregates_background"],
      name: "Macroaggregates Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_TOTAL_CARBON_BACKGROUND]: {
      ids: ["soil_health_total_carbon_background"],
      name: "Total Organic Carbon Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_TOTAL_NITROGEN_BACKGROUND]: {
      ids: ["soil_health_total_nitrogen_background"],
      name: "Total Nitrogen Background",
      defaultClosed: false,
    },
    [SystemContentType.SOIL_HEALTH_WATER_CAPACITY_BACKGROUND]: {
      ids: ["soil_health_water_capacity_background"],
      name: "Water Holding Capacity Background",
      defaultClosed: false,
    },
    [SystemContentType.SATELLITE_NDVI_EXPLAINER]: {
      ids: ["satellite_ndvi_explainer"],
      name: "NDVI Explainer",
      defaultClosed: false,
    },
    [SystemContentType.SATELLITE_BASELINE_NDVI_EXPLAINER]: {
      ids: ["satellite_baseline_ndvi_explainer"],
      name: "Baseline NDVI Explainer",
      defaultClosed: false,
    },
    [SystemContentType.SATELLITE_EVI_EXPLAINER]: {
      ids: ["satellite_evi_explainer"],
      name: "EVI Explainer",
      defaultClosed: false,
    },
    [SystemContentType.SATELLITE_MSAVI_EXPLAINER]: {
      ids: ["satellite_msavi_explainer"],
      name: "MSAVI Explainer",
      defaultClosed: false,
    },
    [SystemContentType.SATELLITE_NDRE_EXPLAINER]: {
      ids: ["satellite_ndre_explainer"],
      name: "NDRE Explainer",
      defaultClosed: false,
    },
    [SystemContentType.SATELLITE_CCCI_EXPLAINER]: {
      ids: ["satellite_ccci_explainer"],
      name: "CCCI Explainer",
      defaultClosed: false,
    },
    [SystemContentType.SATELLITE_NDVI_ANOMALY_EXPLAINER]: {
      ids: ["satellite_ndvi_anomaly_explainer"],
      name: "NDVI Anomaly Explainer",
      defaultClosed: false,
    },
  };

export const isSystemContentType = (key: string): key is SystemContentType => {
  return Object.keys(SYSTEM_CONTENT_SECTIONS).includes(
    key as SystemContentType,
  );
};

export type ModuleSection = {
  // Note, if we were to use `[key in moduleType]?`, then any usage of
  // ModuleSection indexed by a module type returns a ModuleSection | undefined.
  [key in moduleType]: {
    fieldSections: Section[];
    farmSections?: Section[];
    // Array of titles for subsections only shown on the admin page
    subsectionTitles?: string[];
  };
};

export const MODULE_SECTIONS: ModuleSection = {
  [moduleType.DASHBOARD]: {
    fieldSections: [],
    farmSections: [
      {
        ids: ["dashboard_content"],
        name: "Dashboard Content",
      },
      SYSTEM_CONTENT_SECTIONS[SystemContentType.DASHBOARD_SYSTEM_CONTENT],
    ],
  },
  [moduleType.FIELD_PROPERTIES]: {
    fieldSections: [],
  },
  [moduleType.COVER_CROPS]: {
    fieldSections: [
      {
        ids: ["advisor_field_notes"],
        name: "Map",
        hasFieldSelector: true,
      },
      {
        ids: [
          SystemContentType.COVER_CROPS_NUTRIENT_COMPOSITION_BACKGROUND,
          "cover_crop_composition",
        ],
        name: "Cover crop nutrient composition for",
        hasFieldSelector: true,
      },
      {
        ids: ["biomass_cc_variability"],
        name: "Biomass variability on this field",
      },
      {
        ids: ["carbon_cc_variability"],
        name: "Carbon variability on this field",
      },
      {
        ids: ["nitrogen_cc_variability"],
        name: "Nitrogen variability on this field",
      },
      {
        ids: ["cn_ratio_cc_variability"],
        name: "C/N Ratio variability on this field",
      },
      {
        ids: ["phosphorus_cc_variability"],
        name: "Phosphorus variability on this field",
      },
      {
        ids: ["potassium_cc_variability"],
        name: "Potassium variability on this field",
      },
      {
        ids: ["calcium_cc_variability"],
        name: "Calcium variability on this field",
      },
      {
        ids: ["magnesium_cc_variability"],
        name: "Magnesium variability on this field",
      },
      {
        ids: ["sulfur_cc_variability"],
        name: "Sulfur variability on this field",
      },
      {
        ids: ["zinc_cc_variability"],
        name: "Zinc variability on this field",
      },
      {
        ids: ["iron_cc_variability"],
        name: "Iron variability on this field",
      },
      {
        ids: ["manganese_cc_variability"],
        name: "Manganese variability on this field",
      },
      {
        ids: ["copper_cc_variability"],
        name: "Copper variability on this field",
      },
      {
        ids: ["boron_cc_variability"],
        name: "Boron variability on this field",
      },
      {
        ids: ["cover_crop_mix"],
        name: "Cover Crop Mix for",
        hasFieldSelector: true,
      },
    ],
    farmSections: [
      SYSTEM_CONTENT_SECTIONS[SystemContentType.COVER_CROPS_BACKGROUND],
      SYSTEM_CONTENT_SECTIONS[SystemContentType.COVER_CROPS_BENEFITS],
      {
        ids: [SystemContentType.COVER_CROPS_ROI_BACKGROUND, "cover_crop_roi"],
        name: "Return on investment from your cover crops",
      },
      {
        ids: [
          SystemContentType.COVER_CROPS_NUTRIENT_COMPOSITION_BACKGROUND,
          "cover_crop_composition",
        ],
        name: "Cover crop nutrient composition",
      },
      {
        ids: ["biomass_cc_variability"],
        name: "Biomass variability across fields",
      },
      {
        ids: ["carbon_cc_variability"],
        name: "Carbon variability across fields",
      },
      {
        ids: ["nitrogen_cc_variability"],
        name: "Nitrogen variability across fields",
      },
      {
        ids: ["cn_ratio_cc_variability"],
        name: "C/N Ratio variability across fields",
      },
      {
        ids: ["phosphorus_cc_variability"],
        name: "Phosphorus variability across fields",
      },
      {
        ids: ["potassium_cc_variability"],
        name: "Potassium variability across fields",
      },
      {
        ids: ["calcium_cc_variability"],
        name: "Calcium variability across fields",
      },
      {
        ids: ["magnesium_cc_variability"],
        name: "Magnesium variability across fields",
      },
      {
        ids: ["sulfur_cc_variability"],
        name: "Sulfur variability across fields",
      },
      {
        ids: ["zinc_cc_variability"],
        name: "Zinc variability across fields",
      },
      {
        ids: ["iron_cc_variability"],
        name: "Iron variability across fields",
      },
      {
        ids: ["manganese_cc_variability"],
        name: "Manganese variability across fields",
      },
      {
        ids: ["copper_cc_variability"],
        name: "Copper variability across fields",
      },
      {
        ids: ["boron_cc_variability"],
        name: "Boron variability across fields",
      },
      {
        ids: ["termination_plan"],
        name: "Termination Plan",
      },
      {
        ids: ["farm_wide_planting"],
        name: "Planting Guide",
      },
      {
        ids: ["recommended_mixes"],
        name: "Recommended Mixes for the Farm",
      },
      {
        ids: ["advisor_farm_notes"],
        name: "Advisor Notes",
      },
    ],
  },
  [moduleType.NUTRIENT_MANAGEMENT]: {
    farmSections: [
      SYSTEM_CONTENT_SECTIONS[SystemContentType.NUTRIENT_MANAGEMENT_BACKGROUND],
      { ids: ["advisor_farm_notes"], name: "Advisor Notes" },
      {
        ids: ["lime_prescription", "lime_prescription_map"],
        name: "Nutrient prescription for Lime",
        mapInfo: {
          datasource: DATASOURCES.fertilizer_recommendation.id,
          layer: "rx_lime",
        },
      },
      {
        ids: ["phosphorus_prescription", "phosphorus_prescription_map"],
        name: "Nutrient prescription for Phosphorus",
        mapInfo: {
          datasource: DATASOURCES.fertilizer_recommendation.id,
          layer: "rx_p2o5_general",
        },
      },
      {
        ids: ["potassium_prescription", "potassium_prescription_map"],
        name: "Nutrient prescription for Potassium",
        mapInfo: {
          datasource: DATASOURCES.fertilizer_recommendation.id,
          layer: "rx_k2o_general",
        },
      },
      {
        ids: ["sulfur_prescription", "sulfur_prescription_map"],
        name: "Nutrient prescription for Sulfur",
        mapInfo: {
          datasource: DATASOURCES.fertilizer_recommendation.id,
          layer: "rx_s",
        },
      },
      { ids: ["problem_fields"], name: "Identify problem fields" },
      {
        ids: ["nutrient_test_results"],
        name: "Test results for",
        hasFieldSelector: true,
      },
      {
        ids: ["cec_compare_fields", "cec_compare_fields_map"],
        name: "Compare field results for CEC",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_cec_meq_100_g",
        },
      },
      {
        ids: ["ph_water_compare_fields", "ph_water_compare_fields_map"],
        name: "Compare field results for pH (water)",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_ph_h20",
        },
      },
      {
        ids: ["ph_buffer_compare_fields", "ph_buffer_compare_fields_map"],
        name: "Compare field results for pH (buffer)",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_ph_buffer",
        },
      },
      {
        ids: [
          "organic_matter_compare_fields",
          "organic_matter_compare_fields_map",
        ],
        name: "Compare field results for Organic Matter",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vender_om",
        },
      },
      {
        ids: ["phosphate_compare_fields", "phosphate_compare_fields_map"],
        name: "Compare field results for Phosphorus",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_p_ppm",
        },
      },
      {
        ids: ["potassium_compare_fields", "potassium_compare_fields_map"],
        name: "Compare field results for Potassium",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_k_ppm",
        },
      },
      {
        ids: ["calcium_compare_fields", "calcium_compare_fields_map"],
        name: "Compare field results for Calcium",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_ca_ppm",
        },
      },
      {
        ids: ["magnesium_compare_fields", "magnesium_compare_fields_map"],
        name: "Compare field results for Magnesium",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_mg_ppm",
        },
      },
      {
        ids: ["sulfur_compare_fields", "sulfur_compare_fields_map"],
        name: "Compare field results for Sulfur",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_s_ppm",
        },
      },
      {
        ids: ["sodium_compare_fields", "sodium_compare_fields_map"],
        name: "Compare field results for Sodium",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_na_ppm",
        },
      },
      {
        ids: ["zinc_compare_fields", "zinc_compare_fields_map"],
        name: "Compare field results for Zinc",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_zn_ppm",
        },
      },
      {
        ids: ["iron_compare_fields", "iron_compare_fields_map"],
        name: "Compare field results for Iron",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_fe_ppm",
        },
      },
      {
        ids: ["manganese_compare_fields", "manganese_compare_fields_map"],
        name: "Compare field results for Manganese",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_mn_ppm",
        },
      },
      {
        ids: ["copper_compare_fields", "copper_compare_fields_map"],
        name: "Compare field results for Copper",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_cu_ppm",
        },
      },
      {
        ids: ["boron_compare_fields", "boron_compare_fields_map"],
        name: "Compare field results for Boron",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_b_ppm",
        },
      },
      {
        ids: [
          "calcium_saturation_compare_fields",
          "calcium_saturation_compare_fields_map",
        ],
        name: "Compare field results for Calcium Saturation",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_ca_sat_pct",
        },
      },
      {
        ids: [
          "magnesium_saturation_compare_fields",
          "magnesium_saturation_compare_fields_map",
        ],
        name: "Compare field results for Magnesium Saturation",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_mg_sat_pct",
        },
      },
      {
        ids: [
          "potassium_saturation_compare_fields",
          "potassium_saturation_compare_fields_map",
        ],
        name: "Compare field results for Potassium Saturation",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_k_sat_pct",
        },
      },
      {
        ids: [
          "base_saturation_compare_fields",
          "base_saturation_compare_fields_map",
        ],
        name: "Compare field results for Base Saturation",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "base_sat",
        },
      },
      {
        ids: [
          "sodium_saturation_compare_fields",
          "sodium_saturation_compare_fields_map",
        ],
        name: "Compare field results for Sodium Saturation",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_na_sat_pct",
        },
      },
      {
        ids: [
          "hydrogen_saturation_compare_fields",
          "hydrogen_saturation_compare_fields_map",
        ],
        name: "Compare field results for Hydrogen Saturation",
        mapInfo: {
          datasource: DATASOURCES.nutrient_testing.id,
          layer: "vendor_h_sat_pct",
        },
      },
    ],
    fieldSections: [
      { ids: ["advisor_field_notes"], name: "Advisor Notes" },
      {
        ids: ["nutrient_prescription"],
        name: "Vayda's prescription for this field",
      },
      {
        ids: ["nutrient_test_results"],
        name: "Test results for",
        hasFieldSelector: true,
      },
    ],
    subsectionTitles: ["Table", "Map"],
  },
  [moduleType.TILLAGE]: {
    farmSections: [
      { ids: ["advisor_farm_notes"], name: "Advisor Notes" },
      SYSTEM_CONTENT_SECTIONS[SystemContentType.TILLAGE_APPROACH],
      SYSTEM_CONTENT_SECTIONS[SystemContentType.TILLAGE_BACKGROUND],
      {
        ids: ["tillage_plan"],
        name: "Tillage plan for",
        hasFieldSelector: true,
      },
      { ids: ["tillage_map"], name: "Tillage map" },
      SYSTEM_CONTENT_SECTIONS[
        SystemContentType.TILLAGE_UNDERSTANDING_COMPACTION
      ],
      {
        ids: ["compaction_summary_farm", "compaction_map_farm"],
        name: "Compaction Summary for",
        hasFieldSelector: true,
      },
    ],
    fieldSections: [
      { ids: ["advisor_field_notes"], name: "Advisor Notes" },
      {
        ids: ["tillage_plan"],
        name: "Tillage plan for",
        hasFieldSelector: true,
      },
      { ids: ["tillage_map"], name: "Tillage map" },
      {
        ids: ["compaction_summary_field"],
        name: "Compaction Summary for",
        hasFieldSelector: true,
      },
      {
        ids: ["depth_to_compaction_map"],
        name: "Depth where compaction starts",
      },
      {
        ids: ["average_resistance_chart"],
        name: "Average resistance at each inch",
      },
      { ids: ["soil_resistance_2_inch"], name: "Soil Resistance at 2 inches" },
      { ids: ["soil_resistance_4_inch"], name: "Soil Resistance at 4 inches" },
      { ids: ["soil_resistance_6_inch"], name: "Soil Resistance at 6 inches" },
      { ids: ["soil_resistance_8_inch"], name: "Soil Resistance at 8 inches" },
      {
        ids: ["soil_resistance_10_inch"],
        name: "Soil Resistance at 10 inches",
      },
      {
        ids: ["soil_resistance_12_inch"],
        name: "Soil Resistance at 12 inches",
      },
      {
        ids: ["soil_resistance_14_inch"],
        name: "Soil Resistance at 14 inches",
      },
    ],
    subsectionTitles: ["Table", "Map"],
  },
  [moduleType.SOIL_HEALTH]: {
    farmSections: [
      { ids: ["advisor_farm_notes"], name: "Advisor Notes" },
      SYSTEM_CONTENT_SECTIONS[SystemContentType.SOIL_HEALTH_BACKGROUND],
      {
        ids: [SystemContentType.SOIL_HEALTH_ZONES_BACKGROUND, "soil_zones"],
        name: "Soil Health Zones",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_TEST_RESULTS_BACKGROUND,
          "soil_test_results_fields",
        ],
        name: "Test results",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_RESPIRATION_BACKGROUND,
          "soil_results_respiration",
        ],
        name: "Soil Respiration (Haney)",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_WEOC_BACKGROUND,
          "soil_results_weoc",
        ],
        name: "Water Extractable Organic C (Haney)",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_WEON_BACKGROUND,
          "soil_results_weon",
        ],
        name: "Water Extractable Organic N (Haney)",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_H3A_ORGANIC_P_BACKGROUND,
          "soil_results_h3a_organic_p",
        ],
        name: "H3A Organic P (Haney)",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_MACROAGGREGATES_BACKGROUND,
          "soil_results_macroaggregates",
        ],
        name: "Macroaggregates",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_TOTAL_CARBON_BACKGROUND,
          "soil_results_total_carbon",
        ],
        name: "Total Organic Carbon",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_TOTAL_NITROGEN_BACKGROUND,
          "soil_results_total_nitrogen",
        ],
        name: "Total Nitrogen",
      },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_WATER_CAPACITY_BACKGROUND,
          "soil_results_water_capacity",
        ],
        name: "Water Holding Capacity",
      },
    ],
    fieldSections: [
      { ids: ["advisor_field_notes"], name: "Advisor Notes" },
      {
        ids: [
          SystemContentType.SOIL_HEALTH_TEST_RESULTS_BACKGROUND,
          "soil_test_results_fields",
        ],
        name: "Test results",
      },
    ],
  },
  // Implement these so downstream types can be sure they will get a valid
  // value.
  [moduleType.GENERAL]: {
    fieldSections: [],
  },
  [moduleType.NOTES]: {
    fieldSections: [],
  },
};
